"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Flame, Flag, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  api,
  loadTokens,
  type FeedItemRow,
  type MarketListItem,
  type PiLeaderboardRow,
} from "@/lib/api";
import { loginUrl } from "@/lib/loginUrl";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";
import { FeedTabs } from "@/components/social/FeedTabs";
import { V6CreatorsDirectory } from "@/components/v6/CreatorsDirectory";
import {
  categoryIcon,
  formatUsdcCentsForDisplay,
} from "@/lib/format";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useFxRate } from "@/lib/useFxRate";
import { usePickLocale } from "@/lib/usePickLocale";

/**
 * EPIC-006 STORY-604 — personalized feed.
 *
 * Shows markets the user's followees have recently traded on, deduped
 * per market with the most-recent followee trade as attribution.
 *
 * Empty state nudges the user toward the leaderboard / market list to
 * find people to follow.
 */
export default function FeedPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const [authChecked, setAuthChecked] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [items, setItems] = useState<FeedItemRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loadTokens()) {
      router.replace(loginUrl());
      return;
    }
    void api
      .me()
      .then(() => {
        setAuthChecked(true);
        setAuthorized(true);
      })
      .catch(() => router.replace(loginUrl()));
  }, [router]);

  useEffect(() => {
    if (!authorized) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    void api
      .listMyFeed()
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setNextCursor(res.nextCursor);
      })
      .catch((err) => {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : t("feedPage.loadFailed"),
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // t is the i18next singleton — stable across renders. Listing it
    // would re-fetch on every locale toggle; the existing fetch is
    // already locale-independent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized]);

  async function loadMore() {
    if (!nextCursor || loading) return;
    setLoading(true);
    try {
      const res = await api.listMyFeed({ cursor: nextCursor });
      setItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("feedPage.loadMoreFailed"),
      );
    } finally {
      setLoading(false);
    }
  }

  if (!authChecked) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto max-w-[800px] p-8 text-sm text-[var(--color-text3)]">
          {t("feedPage.loading")}
        </main>
      </>
    );
  }
  if (!authorized) return null;

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0 max-w-3xl">
          <div className="mb-4">
            <h1 className="text-[22px] font-bold tracking-tight">
              {t("feedPage.title")}
            </h1>
            <p className="mt-1 text-[12px] text-[var(--color-text3)]">
              {t("feedPage.subtitle")}
            </p>
          </div>
          <FeedTabs active="trades" />

          {error && (
            <p
              role="alert"
              className="mb-4 text-[12px]"
              style={{ color: "var(--color-no)" }}
            >
              {error}
            </p>
          )}

          {!loading && items.length === 0 && !error && <DiscoverEmptyState />}

          {loading && items.length === 0 && (
            <p className="text-[12px] text-[var(--color-text3)]">
              {t("feedPage.loading")}
            </p>
          )}

          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item.marketId}>
                <FeedCard item={item} t={t} />
              </li>
            ))}
          </ul>

          {nextCursor && (
            <button
              type="button"
              disabled={loading}
              onClick={loadMore}
              className="mt-4 w-full rounded-md border border-[var(--color-border)] py-2 text-[12px] font-bold text-[var(--color-text2)] hover:border-[var(--color-brand)]"
            >
              {loading ? t("feedPage.loading") : t("feedPage.loadMore")}
            </button>
          )}

          {/* 2026-05-24 — embedded Creators directory. Sits below the
              feed so users discover paid + free creators without
              leaving /feed. Pulls from lib/v6/mockCreators for now;
              swap for a real /v1/creators endpoint when wired. */}
          <V6CreatorsDirectory limit={6} />
        </main>
      </div>
    </>
  );
}

function FeedCard({ item, t }: { item: FeedItemRow; t: TFunction }) {
  const pick = usePickLocale();
  const title =
    typeof item.marketTitle === "string"
      ? item.marketTitle
      : pick(item.marketTitle);
  const initial = (item.byDisplayName || item.byUsername)
    .charAt(0)
    .toUpperCase();
  const sideLabel =
    item.bySide === "buy" ? t("feedPage.boughtSide") : t("feedPage.soldSide");
  const sideColor =
    item.bySide === "buy" ? "var(--color-yes)" : "var(--color-no)";

  return (
    <article className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <header className="mb-2 flex items-baseline gap-2 text-[12px]">
        <Link
          href={`/u/${item.byUsername}`}
          className="flex items-baseline gap-2 hover:text-[var(--color-brand)]"
        >
          <span
            className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white"
            style={{ background: "linear-gradient(135deg, #8957e5, #ec4899)" }}
            aria-hidden="true"
          >
            {initial}
          </span>
          <span className="font-bold text-[var(--color-text)]">
            {item.byDisplayName || item.byUsername}
          </span>
        </Link>
        <span className="text-[var(--color-text3)]">
          {sideLabel}{" "}
          <span style={{ color: sideColor }}>
            {t("feedPage.tradeLine", {
              shares: item.bySizeShares,
              price: item.byPriceCents,
            })}
          </span>
        </span>
        <span className="ml-auto text-[10px] text-[var(--color-text3)]">
          {fmtRelative(item.byTradedAt, t)}
        </span>
      </header>

      <Link
        href={`/markets/${item.marketSlug}`}
        className="block rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3 hover:border-[var(--color-brand)]"
      >
        <h3 className="text-[14px] font-bold text-[var(--color-text)]">
          {title}
        </h3>
        <div className="mt-1 flex items-center gap-3 text-[11px] text-[var(--color-text3)]">
          {item.yesPriceCents !== null && (
            <span className="font-mono tabular-nums">
              YES {item.yesPriceCents}¢
            </span>
          )}
          <span>
            {t("feedPage.closesPrefix", { rel: fmtRelative(item.closesAt, t) })}
          </span>
          <span className="ml-auto rounded-[4px] border border-[var(--color-border)] px-1 py-[1px] text-[9px] uppercase tracking-[0.4px]">
            {item.marketStatus}
          </span>
        </div>
      </Link>
    </article>
  );
}

function fmtRelative(iso: string, t: TFunction): string {
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const past = ms < 0;
  const mins = Math.floor(abs / 60_000);
  if (mins < 1)
    return past ? t("feedPage.rel.now") : t("feedPage.rel.underMinFuture");
  if (mins < 60)
    return past
      ? t("feedPage.rel.minsAgo", { n: mins })
      : t("feedPage.rel.minsFuture", { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)
    return past
      ? t("feedPage.rel.hoursAgo", { n: hrs })
      : t("feedPage.rel.hoursFuture", { n: hrs });
  const d = Math.floor(hrs / 24);
  if (d < 7)
    return past
      ? t("feedPage.rel.daysAgo", { n: d })
      : t("feedPage.rel.daysFuture", { n: d });
  return new Date(iso).toLocaleDateString();
}

const FEATURED_CATEGORY_SLUGS = [
  "politics",
  "sports",
  "crypto",
  "economics",
  "tech",
] as const;

const CATEGORY_KEY: Record<(typeof FEATURED_CATEGORY_SLUGS)[number], string> = {
  politics: "feedPage.discover.categoryPolitics",
  sports: "feedPage.discover.categorySports",
  crypto: "feedPage.discover.categoryCrypto",
  economics: "feedPage.discover.categoryEconomics",
  tech: "feedPage.discover.categoryTech",
};

/**
 * Empty state for /feed when the viewer doesn't follow anyone yet (or
 * nobody they follow has traded in the last 14 days).
 *
 * Goal: feel intentional, not broken. Pull platform-wide signal from
 * existing endpoints rather than rendering a single dead-end nudge.
 *
 * Sections:
 *   1. Trending now — top 4 active markets by volume
 *   2. Recent big resolutions — top 3 resolved markets by volume
 *   3. Popular Investors to follow — top 3 from the leaderboard
 *   4. Browse by category — quick-jump chips
 *
 * All four fetches run in parallel; partial failures degrade
 * silently (other sections still render). No backend changes —
 * reuses listMarkets({sort:trending}), listMarkets({status:resolved}),
 * and getPiLeaderboard.
 */
function DiscoverEmptyState() {
  const { t } = useTranslation();
  const [trending, setTrending] = useState<MarketListItem[]>([]);
  const [resolved, setResolved] = useState<MarketListItem[]>([]);
  const [pis, setPis] = useState<PiLeaderboardRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([
      api.listMarkets({ status: "active", sort: "trending", limit: 4 }),
      api.listMarkets({ status: "resolved", sort: "trending", limit: 3 }),
      api.getPiLeaderboard(3),
    ]).then(([tr, rs, ps]) => {
      if (cancelled) return;
      if (tr.status === "fulfilled") setTrending(tr.value.markets);
      if (rs.status === "fulfilled") setResolved(rs.value.markets);
      if (ps.status === "fulfilled") setPis(ps.value.rows);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <h2 className="text-[16px] font-bold tracking-tight">
          {t("feedPage.discover.welcome")}
        </h2>
        <p className="mt-1 text-[12px] text-[var(--color-text3)]">
          {t("feedPage.discover.welcomeBody")}
        </p>
      </section>

      {trending.length > 0 && (
        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <h3 className="flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.6px] text-[var(--color-text2)]">
              <Flame size={14} strokeWidth={1.75} aria-hidden="true" />
              {t("feedPage.discover.trendingNow")}
            </h3>
            <Link
              href="/markets?sort=trending"
              className="text-[11px] text-[var(--color-text3)] hover:text-[var(--color-brand)]"
            >
              {t("feedPage.discover.seeAll")}
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {trending.map((m) => (
              <DiscoverMarketCard key={m.id} m={m} t={t} />
            ))}
          </div>
        </section>
      )}

      {resolved.length > 0 && (
        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <h3 className="flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.6px] text-[var(--color-text2)]">
              <Flag size={14} strokeWidth={1.75} aria-hidden="true" />
              {t("feedPage.discover.recentResolutions")}
            </h3>
            <Link
              href="/markets?status=resolved"
              className="text-[11px] text-[var(--color-text3)] hover:text-[var(--color-brand)]"
            >
              {t("feedPage.discover.seeAll")}
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {resolved.map((m) => (
              <DiscoverMarketCard key={m.id} m={m} resolved t={t} />
            ))}
          </div>
        </section>
      )}

      {pis.length > 0 && (
        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <h3 className="flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.6px] text-[var(--color-text2)]">
              <Sparkles size={14} strokeWidth={1.75} aria-hidden="true" />
              {t("feedPage.discover.popularToFollow")}
            </h3>
            <Link
              href="/pi/leaderboard"
              className="text-[11px] text-[var(--color-text3)] hover:text-[var(--color-brand)]"
            >
              {t("feedPage.discover.seeLeaderboard")}
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {pis.map((p) => (
              <DiscoverPiCard key={p.userId} p={p} t={t} />
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className="mb-2 text-[13px] font-bold uppercase tracking-[0.6px] text-[var(--color-text2)]">
          {t("feedPage.discover.browseByCategory")}
        </h3>
        <div className="flex flex-wrap gap-2">
          {FEATURED_CATEGORY_SLUGS.map((slug) => {
            const Icon = categoryIcon(slug);
            return (
              <Link
                key={slug}
                href={`/markets?category=${slug}`}
                className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-[12px] font-bold text-[var(--color-text2)] transition-colors hover:border-[var(--color-brand)] hover:text-[var(--color-brand)] md:min-h-0"
              >
                <Icon size={12} strokeWidth={1.75} aria-hidden="true" />
                {t(CATEGORY_KEY[slug])}
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function DiscoverMarketCard({
  m,
  resolved,
  t,
}: {
  m: MarketListItem;
  resolved?: boolean;
  t: TFunction;
}) {
  const pick = usePickLocale();
  const title = typeof m.title === "string" ? m.title : pick(m.title);
  const displayCurrency = useDisplayCurrency();
  const fxBrl = useFxRate("BRL");
  const usdcPerBrl = fxBrl?.rate ?? null;
  const volSrcCents = Number(m.totalVolumeCents);
  const volLabel = formatUsdcCentsForDisplay(
    volSrcCents,
    displayCurrency,
    usdcPerBrl,
    { compact: true },
  );
  return (
    <Link
      href={`/markets/${m.slug}`}
      className="block rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 transition-colors hover:border-[var(--color-brand)]"
    >
      <p className="line-clamp-2 text-[13px] font-bold text-[var(--color-text)]">
        {title}
      </p>
      <div className="mt-2 flex items-center gap-3 text-[11px] text-[var(--color-text3)]">
        {!resolved && m.yesPriceCents !== null && (
          <span className="font-mono tabular-nums text-[var(--color-yes)]">
            YES {m.yesPriceCents}¢
          </span>
        )}
        <span className="font-mono tabular-nums">{volLabel}</span>
        <span className="ml-auto rounded-[4px] border border-[var(--color-border)] px-1 py-[1px] text-[9px] uppercase tracking-[0.4px]">
          {resolved ? t("feedPage.discover.resolved") : m.categorySlug}
        </span>
      </div>
    </Link>
  );
}

function DiscoverPiCard({ p, t }: { p: PiLeaderboardRow; t: TFunction }) {
  const initial = p.username.charAt(0).toUpperCase();
  const displayCurrency = useDisplayCurrency();
  const fxBrl = useFxRate("BRL");
  const usdcPerBrl = fxBrl?.rate ?? null;
  const volSrcCents = Number(p.lifetimeVolumeCents);
  const volLabel = formatUsdcCentsForDisplay(
    volSrcCents,
    displayCurrency,
    usdcPerBrl,
    { compact: true },
  );
  const subWord =
    p.activeSubscriberCount === 1
      ? t("feedPage.discover.subSingular")
      : t("feedPage.discover.subPlural");
  return (
    <Link
      href={`/pi/${p.username}`}
      className="block rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 transition-colors hover:border-[var(--color-brand)]"
    >
      <div className="flex items-center gap-2">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-bold text-white"
          style={{ background: "linear-gradient(135deg, #8957e5, #ec4899)" }}
          aria-hidden="true"
        >
          {initial}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-bold text-[var(--color-text)]">
            @{p.username}
          </p>
          {p.piTier && (
            <p className="text-[10px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
              {p.piTier} · {p.activeSubscriberCount} {subWord}
            </p>
          )}
        </div>
      </div>
      <p className="mt-2 text-[11px] text-[var(--color-text3)]">
        {t("feedPage.discover.piRowSummary", {
          trades: p.lifetimeTradeCount,
          volume: volLabel,
        })}
      </p>
    </Link>
  );
}
